'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { useCartStore, useShallow } from './store/CartStoreContext';
import { CartEmptyState } from './components';
import { ErrorBoundary } from '../ErrorBoundary';
import { RouteIntlScope } from 'lib/i18n/routeIntl';
import styles from './index.module.css';

function CartLoadingStatus() {
  const t = useTranslations('common');
  return (
    <div
      role="status"
      aria-label={t('cart.content.ariaLabel.loadingCart')}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 240,
        padding: 24,
      }}
    />
  );
}

const CartContent = dynamic(
  () => import('./Cart').then((m) => m.CartContent),
  {
    ssr: false,
    loading: () => <CartLoadingStatus />,
  },
);

// FIXME: Simplify cart state/validation by normalizing around Shopify cart responses and reducing duplicate client-side checks.


export const Cart = () => {
  const { cart, isCartVisible, altContent, hideCart } = useCartStore(
    useShallow((s) => ({
      cart: s.cart,
      isCartVisible: s.isCartVisible,
      altContent: s.altContent,
      hideCart: s.hideCart,
    })),
  );

  return (
    <div
      className={`${styles.root} ${isCartVisible ? 'active' : ''}`}
      onClick={hideCart}
      aria-hidden="true"
      data-testid="cart"
    >
      <div
        className={styles.content}
        onClick={(e) => {
          e.stopPropagation();
        }}
        aria-hidden="true"
      >
        {cart &&
          cart?.lines &&
          cart?.lines.edges &&
          cart?.lines.edges.length >= 1 && (
            <ErrorBoundary
              name="cart-content"
              // Reset error state when line count changes (avoid sticky fallback after transient error).
              resetKeys={[cart.lines.edges.length]}
              fallback={<CartEmptyState onBackToBrowse={hideCart} />}
            >
              <CartContent />
            </ErrorBoundary>
          )}

        {(!cart ||
          !cart?.lines ||
          !cart?.lines.edges ||
          !(cart?.lines.edges.length >= 1)) && (
          <CartEmptyState onBackToBrowse={hideCart} />
        )}

        {altContent && (
          <div className={styles.overlayContent}>
            <h2>
              &nbsp;
              <button className={styles.closeBtn} onClick={hideCart ?? null}>
                ×
              </button>
            </h2>
            <RouteIntlScope>{altContent}</RouteIntlScope>
          </div>
        )}
      </div>
    </div>
  );
};

export * from './context';
export { getShippingAndTaxDisclaimerMap } from './helper';
